import { Component, ViewChild, ElementRef, AfterViewChecked, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DingoAiChatService, DingoAiMessage } from './dingo-ai-chat.service';

const TYPEWRITER_TICK_MS = 16;
const TYPEWRITER_MAX_DURATION_MS = 900;
const TYPEWRITER_MIN_CHUNK = 4;

@Component({
  selector: 'app-dingo-ai-widget',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dingo-ai-widget.component.html',
  styleUrl: './dingo-ai-widget.component.scss',
})
export class DingoAiWidgetComponent implements AfterViewChecked, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;

  isOpen = false;
  sending = false;
  error: string | null = null;
  input = '';

  /** Índice del mensaje del asistente que se está “escribiendo” (-1 = ninguno). */
  typingMessageIndex = -1;
  /** Cantidad de caracteres ya mostrados para ese mensaje. */
  typingCharCount = 0;
  private typingIntervalId: ReturnType<typeof setInterval> | null = null;
  private scrollToBottomRequested = false;
  private didTypeInitialMessage = false;
  readonly quickActions: Array<{ label: string; type: 'prompt' | 'whatsapp' | 'specialist'; value: string }> = [
    {
      label: 'Más clientes',
      type: 'prompt',
      value: 'Quiero conseguir más clientes. Ayúdame con una ruta clara para lograrlo.',
    },
    {
      label: 'App o plataforma',
      type: 'prompt',
      value: 'Quiero desarrollar una app o plataforma para mi negocio.',
    },
    {
      label: 'Mejorar marketing',
      type: 'prompt',
      value: 'Quiero mejorar mi marketing actual y subir resultados.',
    },
    {
      label: 'Estrategia completa',
      type: 'prompt',
      value: 'Quiero una estrategia completa para crecer mi negocio.',
    },
    {
      label: 'WhatsApp',
      type: 'whatsapp',
      value: '',
    },
  ];

  messages: DingoAiMessage[] = [
    {
      role: 'assistant',
      content:
        'Hola, soy el asistente de Grupo DINGO.\n\nEn 2 minutos puedo decirte cómo crecer tu negocio.\n\n¿Qué te interesa?',
    },
  ];

  constructor(private chat: DingoAiChatService) {}

  /**
   * Normaliza texto de backend para evitar que se vean escapes literales
   * como "\\n" en lugar de saltos de línea reales.
   */
  private normalizeAssistantText(text: string): string {
    return (text || '')
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '  ');
  }

  ngAfterViewChecked() {
    if (this.scrollToBottomRequested && this.messagesContainer?.nativeElement) {
      this.scrollToBottomRequested = false;
      this.scrollToBottom();
    }
  }

  ngOnDestroy() {
    this.stopTypewriter();
  }

  private scrollToBottom() {
    const el = this.messagesContainer?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }

  toggle() {
    this.isOpen = !this.isOpen;
    this.error = null;

    // Al cerrar, frenamos la animación para evitar estados raros.
    if (!this.isOpen) {
      this.stopTypewriter();
      this.typingMessageIndex = -1;
      this.typingCharCount = 0;
      return;
    }

    // Al abrir por primera vez, mostramos el mensaje inicial con animación.
    if (!this.didTypeInitialMessage) {
      const initialAssistantIndex = this.messages.findIndex((m) => m.role === 'assistant');
      if (initialAssistantIndex >= 0) {
        this.didTypeInitialMessage = true;
        this.startTypewriterEffect(initialAssistantIndex);
      }
    }
  }

  /** Devuelve el texto a mostrar para un mensaje del asistente (efecto máquina de escribir). */
  getDisplayedContent(msg: DingoAiMessage, index: number): string {
    if (msg.role !== 'assistant') return msg.content;
    const full = msg.content;
    if (this.typingMessageIndex !== index) return full;
    return full.slice(0, this.typingCharCount);
  }

  /** Indica si el mensaje en index es el que está en animación de escritura. */
  isTypingMessage(index: number): boolean {
    return this.typingMessageIndex === index && this.typingCharCount < (this.messages[index]?.content?.length ?? 0);
  }

  private startTypewriterEffect(messageIndex: number) {
    this.stopTypewriter();
    const msg = this.messages[messageIndex];
    if (!msg || msg.role !== 'assistant') return;
    const full = msg.content;
    if (!full) return;

    this.typingMessageIndex = messageIndex;
    this.typingCharCount = 0;
    this.scrollToBottomRequested = true;
    const steps = Math.max(1, Math.ceil(TYPEWRITER_MAX_DURATION_MS / TYPEWRITER_TICK_MS));
    const chunk = Math.max(TYPEWRITER_MIN_CHUNK, Math.ceil(full.length / steps));

    this.typingIntervalId = setInterval(() => {
      this.typingCharCount += chunk;
      if (this.typingCharCount >= full.length) {
        this.typingCharCount = full.length;
        this.stopTypewriter();
        this.typingMessageIndex = -1;
      }
      this.scrollToBottomRequested = true;
    }, TYPEWRITER_TICK_MS);
  }

  private stopTypewriter() {
    if (this.typingIntervalId) {
      clearInterval(this.typingIntervalId);
      this.typingIntervalId = null;
    }
  }

  async send() {
    const text = this.input.trim();
    if (!text || this.sending) return;
    this.error = null;

    this.messages.push({ role: 'user', content: text });
    this.input = '';
    this.sending = true;
    this.scrollToBottomRequested = true;

    try {
      const result = await this.chat.sendChat(this.messages);
      this.messages.push({
        role: 'assistant',
        content: this.normalizeAssistantText(result.reply),
        suggestWhatsapp: result.suggestWhatsapp,
        whatsappUrl: result.whatsappUrl,
      });
      const newIndex = this.messages.length - 1;
      this.startTypewriterEffect(newIndex);
    } catch (e) {
      console.error(e);
      this.error = 'No pude conectar con Dingo AI. Intenta de nuevo en unos momentos.';
    } finally {
      this.sending = false;
    }
  }

  onInputKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter') return;
    // Enter envía mensaje; Shift+Enter permite salto de línea.
    if (event.shiftKey) return;
    event.preventDefault();
    if (!this.sending && this.input.trim()) {
      void this.send();
    }
  }

  showQuickActions(): boolean {
    const hasUserMessages = this.messages.some((m) => m.role === 'user');
    return this.isOpen && !hasUserMessages && !this.sending;
  }

  selectQuickAction(prompt: string) {
    if (this.sending) return;
    this.input = prompt;
    void this.send();
  }

  handleQuickAction(action: { label: string; type: 'prompt' | 'whatsapp' | 'specialist'; value: string }) {
    if (action.type === 'whatsapp') {
      this.openWhatsapp();
      return;
    }
    if (action.type === 'specialist') {
      this.goToSpecialist();
      return;
    }
    this.selectQuickAction(action.value);
  }

  shouldShowClosingCtas(): boolean {
    const userMessagesCount = this.messages.filter((m) => m.role === 'user').length;
    const assistantMessagesCount = this.messages.filter((m) => m.role === 'assistant').length;
    return this.isOpen && !this.sending && userMessagesCount >= 1 && assistantMessagesCount >= 2;
  }

  openWhatsapp(url?: string) {
    const fallbackPhone = '5215616635041';
    const fallbackMsg =
      'Hola, vengo del sitio web y quiero hablar con un asesor de Grupo DINGO para una cotización / proyecto.';
    const fallbackUrl = `https://wa.me/${fallbackPhone}?text=${encodeURIComponent(fallbackMsg)}`;
    window.open(url || fallbackUrl, '_blank');
  }

  goToSpecialist() {
    window.location.href = '/contacto';
  }
}
